import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

interface Account {
  id: number;
  clinicName: string;
  contactPerson: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  loginId: string;
  password: string;
}

const TempAccountList: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [dialogType, setDialogType] = useState<'detail' | 'edit' | null>(null);

  // データを取得する関数
  const fetchData = async () => {
    const response = await fetch(`https://xs525443.xsrv.jp/kansaibou/clinic_list_api.php?_=${new Date().getTime()}`);
    const data = await response.json();
    setAccounts(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 詳細ダイアログを開く
  const handleDetail = (account: Account) => {
    setSelectedAccount(account);
    setDialogType('detail');
  };

  // 編集ダイアログを開く
  const handleEdit = (account: Account) => {
    setSelectedAccount(account);
    setDialogType('edit');
  };

  // 編集内容を保存
  const handleSave = async () => {
    if (selectedAccount) {
      const updatedAccount = { ...selectedAccount };

      await fetch('https://xs525443.xsrv.jp/kansaibou/clinic_update_api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedAccount),
      });
      

      // ローカルステートを更新
      setAccounts((prevAccounts) =>
        prevAccounts.map((account) => (account.id === updatedAccount.id ? updatedAccount : account))
      );

      setDialogType(null);
      setSelectedAccount(null);

      alert('編集内容が保存されました');
    }
  };

  // データ削除
  const handleDelete = async (id: number) => {
    if (window.confirm('本当に削除しますか？')) {
      await fetch('https://xs525443.xsrv.jp/kansaibou/clinic_delete_api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });

      // ローカルステートを更新
      setAccounts((prevAccounts) => prevAccounts.filter((account) => account.id !== id));

      alert('データが削除されました');
    }
  };

  // ダイアログを閉じる
  const handleClose = () => {
    setSelectedAccount(null);
    setDialogType(null);
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        クリニック一覧
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>クリニック名</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>連絡先メールアドレス</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>{account.clinicName}</TableCell>
                <TableCell>{account.address}</TableCell>
                <TableCell>{account.email}</TableCell>
                <TableCell>
                  <Button variant="contained" color="primary" sx={{ mr: 1 }} onClick={() => handleDetail(account)}>
                    詳細
                  </Button>
                  <Button variant="contained" color="primary" sx={{ mr: 1 }} onClick={() => handleEdit(account)}>
                    編集
                  </Button>
                  <Button variant="contained" color="secondary" onClick={() => handleDelete(account.id)}>
                    削除
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 詳細ダイアログ */}
      <Dialog open={dialogType === 'detail'} onClose={handleClose}>
        <DialogTitle>詳細情報</DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <>
              <Typography>クリニック名: {selectedAccount.clinicName}</Typography>
              <Typography>担当者名: {selectedAccount.contactPerson}</Typography>
              <Typography>住所: {selectedAccount.address}</Typography>
              <Typography>メールアドレス: {selectedAccount.email}</Typography>
              <Typography>電話番号: {selectedAccount.phone}</Typography>
              <Typography>HP: {selectedAccount.website}</Typography>
              <Typography>ログインID: {selectedAccount.loginId}</Typography>
              <Typography>ログインPASS: {selectedAccount.password}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={dialogType === 'edit'} onClose={handleClose}>
        <DialogTitle>編集</DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <>
              <TextField
                label="クリニック名"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.clinicName}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, clinicName: e.target.value })}
              />
              <TextField
                label="担当者名"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.contactPerson}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, contactPerson: e.target.value })}
              />
              <TextField
                label="住所"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.address}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, address: e.target.value })}
              />
              <TextField
                label="メールアドレス"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.email}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, email: e.target.value })}
              />
              <TextField
                label="電話番号"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.phone}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, phone: e.target.value })}
              />
              <TextField
                label="HP"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.website}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, website: e.target.value })}
              />
              <TextField
                label="ログインID"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.loginId}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, loginId: e.target.value })}
              />
              <TextField
                label="ログインPASS"
                fullWidth
                margin="normal"
                defaultValue={selectedAccount.password}
                onChange={(e) => setSelectedAccount({ ...selectedAccount, password: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            キャンセル
          </Button>
          <Button onClick={handleSave} color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TempAccountList;
